/**
 * Nút yêu thích (tim) — toggle qua FavoriteContext.
 *
 * Chưa đăng nhập: mở LoginDialog, thực hiện toggle sau khi login thành công.
 * Variant detail hiển thị nhãn text; default chỉ icon.
 */
import { useState } from 'react';
import { useFavorites } from '../../context/FavoriteContext';
import { useAuth } from '../../hooks/useAuth';
import { useLoginDialog } from '../../hooks/useLoginDialog';
import { TEXT } from '../../shared/languages';

/**
 * Nút lưu/xóa yêu thích.
 * @param {Object} props
 * @param {number|string} props.propertyId
 * @param {string} [props.className]
 * @param {'default'|'detail'} [props.variant]
 */
export const FavoriteButton = ({ propertyId, className = '', variant = 'default' }) => {
  const { isAuthenticated } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { openLoginDialog } = useLoginDialog();
  const [isPending, setIsPending] = useState(false);
  const active = isFavorite(propertyId);

  const saveFavorite = async () => {
    setIsPending(true);

    try {
      await toggleFavorite(propertyId);
    } finally {
      setIsPending(false);
    }
  };

  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isAuthenticated) {
      openLoginDialog({ onSuccess: saveFavorite });
      return;
    }

    await saveFavorite();
  };

  const label = active ? TEXT.favorites.removeLabel : TEXT.property.detail.saveFavorite;

  const button =
    variant === 'detail' ? (
      <button
        type="button"
        className={`detail-action-btn ${active ? 'is-active' : ''} ${className}`.trim()}
        onClick={handleClick}
        disabled={isPending}
        aria-label={active ? TEXT.favorites.removeLabel : TEXT.favorites.saveLabel}
        aria-pressed={active}
      >
        <svg viewBox="0 0 32 32" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? 2 : 2.5}>
          <path d="M16 28c-.2 0-.4-.1-.5-.2C7.5 21.1 2 16.2 2 10.5 2 6.4 5.4 3 9.5 3c2.4 0 4.7 1.2 6.1 3.1C17 4.2 19.3 3 21.7 3 25.8 3 29 6.4 29 10.5c0 5.7-5.5 10.6-13.5 17.3-.1.1-.3.2-.5.2z" />
        </svg>
        {isPending ? TEXT.property.detail.savingFavorite : label}
      </button>
    ) : (
      <button
        type="button"
        className={`favorite-btn favorite-btn--${variant} ${active ? 'is-active' : ''} ${className}`.trim()}
        onClick={handleClick}
        disabled={isPending}
        aria-label={active ? TEXT.favorites.removeLabel : TEXT.favorites.saveLabel}
        aria-pressed={active}
      >
        <svg viewBox="0 0 32 32" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? 2 : 2.5}>
          <path d="M16 28c-.2 0-.4-.1-.5-.2C7.5 21.1 2 16.2 2 10.5 2 6.4 5.4 3 9.5 3c2.4 0 4.7 1.2 6.1 3.1C17 4.2 19.3 3 21.7 3 25.8 3 29 6.4 29 10.5c0 5.7-5.5 10.6-13.5 17.3-.1.1-.3.2-.5.2z" />
        </svg>
      </button>
    );

  return button;
};
